import {Box, Button, Flex, Text, Textarea} from '@chakra-ui/react';
import {Checkbox} from 'components/shared';
import {FC, useRef, useState} from 'react';
import {TranslationKeys} from 'translations';
import ImageUI from 'ui/ImageUI';

import {Grade} from '../Grade';

interface IProps {
  title: string;
  titleImage: string;
  name: string;
  onChangeGrade: (grade: number, comment?: string) => void;
  t: (term: TranslationKeys) => string;
  isDisabledForm?: boolean;
  onImageUpload?: (imageObj: any) => void;
  imageObj?: any;
}

const fileToDataUri = (file: File) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (event: any) => {
      resolve(event.target.result);
    };
    reader.readAsDataURL(file);
  });

export const RateForm: FC<IProps> = ({
  name,
  onChangeGrade,
  t,
  title,
  titleImage,
  isDisabledForm,
  onImageUpload,
  imageObj,
}) => {
  const [grade, setGrade] = useState<number>();
  const [comment, setComment] = useState<string>();
  const inputRef = useRef<HTMLInputElement>(null);

  const showUploadImageForm = !!onImageUpload;
  const onHandlerSendCourierGrade = () => {
    if (grade) {
      onChangeGrade(grade, comment);
    }
  };

  const handleUploadButtonClick = () => {
    inputRef?.current?.click();
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onImageUpload && e.target.files) {
      const file = e.target.files[0];
      const uri = await fileToDataUri(file);
      onImageUpload({
        uri,
        name: file.name.slice(0, 30),
        type: 'image/jpeg',
      });
    }
  };

  const handleRemoveImage = () => {
    if (imageObj && onImageUpload) {
      onImageUpload(undefined);
    }
  };

  return (
    <Flex flexDirection="column" alignItems="center">
      <ImageUI
        w="110px"
        h="110px"
        src={titleImage}
        alt="courier"
        objectFit="cover"
        borderRadius="16px"
        overflow="hidden"
        boxShadow="2px 3px 10px rgba(151, 151, 151, 0.3);"
      />
      <Text textAlign="center" mt="32px">{`${title} ${name}`}</Text>
      <Grade rating={grade} onChange={setGrade} t={t} />
      {grade && (
        <Box mb={8} w="full">
          <Text textAlign="center" mb="16px">
            {t('WHAT_CAN_IMPROVE')}
          </Text>
          <Textarea
            placeholder={t('ADD_COMMENT')}
            flex="1"
            name="comment"
            onChange={(e) => setComment(e.target.value)}
            minHeight="100px"
            mb="16px"
          />
          {showUploadImageForm && (
            <>
              {imageObj ? (
                <Box display="flex" justifyContent="right" alignItems="center">
                  <Checkbox variant="circle" isChecked isDisabled mr={2} />
                  <Text textAlign="center" fontSize="14px">
                    {imageObj.name}
                  </Text>
                  <Button
                    minW="30px"
                    h="30px"
                    borderRadius="50%"
                    bg="palette.lightGray"
                    p={0}
                    ml={4}
                    onClick={handleRemoveImage}
                  >
                    <ImageUI src="/delete-icon.svg" alt="delete" />
                  </Button>
                </Box>
              ) : (
                <>
                  <input
                    ref={inputRef}
                    type="file"
                    accept=".jpg, .jpeg, .png"
                    style={{display: 'none'}}
                    onChange={handleUpload}
                  />
                  <Button
                    variant="gray"
                    leftIcon={
                      imageObj ? (
                        <Checkbox variant="circle" isChecked isDisabled />
                      ) : (
                        <ImageUI w="25px" h="25px" src="/icons/attachment.svg" />
                      )
                    }
                    h="40px"
                    px="20px"
                    display="flex"
                    ml="auto"
                    fontSize="14px"
                    onClick={handleUploadButtonClick}
                  >
                    {t('ATTACH_FILE')}
                  </Button>
                </>
              )}
            </>
          )}
        </Box>
      )}
      <Button
        variant="primary"
        h="50px"
        w="100%"
        onClick={onHandlerSendCourierGrade}
        isDisabled={(!grade && !comment) || isDisabledForm}
      >
        {t('ORDER_EVALUATED')}
      </Button>
    </Flex>
  );
};
